import { useState, useRef, useCallback, useEffect } from 'react';
import { getBrowserBackendUrl } from '../utils/config';

// The hook is now only responsible for the Twilio Video connection.
const useWebRTC = (sessionId, onStreamReady = () => {}) => {
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState(null);
    const [connectionState, setConnectionState] = useState('disconnected');
    const [remoteParticipant, setRemoteParticipant] = useState(null);

    const videoRef = useRef(null);
    const roomRef = useRef(null);

    // ensure we only call the ready callback once
    const readyRef = useRef(false);
    const onStreamReadyRef = useRef(onStreamReady);
    useEffect(() => { onStreamReadyRef.current = onStreamReady; }, [onStreamReady]);

    const markReadyOnce = () => {
        if (!readyRef.current) {
            readyRef.current = true;
            setIsConnected(true);
            onStreamReadyRef.current();
        }
    };

    const handleTrackSubscribed = useCallback((track) => {
        if (track.kind === 'video') {
            // attach to video element
            if (videoRef.current) track.attach(videoRef.current);
            // Some tracks emit "started" after first frame
            track.once?.('started', markReadyOnce);
            // Fallback: mark ready immediately (will be overwritten if above fires later)
            markReadyOnce();
        }
    }, []);

    const handleTrackUnsubscribed = useCallback((track) => {
        if (track.kind === 'video') {
            track.detach();
        }
    }, []);

    const handleParticipantConnected = useCallback((participant) => {
        if (participant.identity.startsWith('remote-browser-')) {
            setRemoteParticipant(participant);
            participant.tracks.forEach(publication => {
                if (publication.track) {
                    handleTrackSubscribed(publication.track);
                    // Set connected to true when we have a video track from the remote browser
                    if (publication.track.kind === 'video') {
                        setIsConnected(true);
                    }
                }
            });
            participant.on('trackSubscribed', (track) => {
                handleTrackSubscribed(track);
            });
            participant.on('trackUnsubscribed', handleTrackUnsubscribed);
        }
    }, [handleTrackSubscribed, handleTrackUnsubscribed]);

    const handleParticipantDisconnected = useCallback((participant) => {
        if (participant.identity.startsWith('remote-browser-')) {
            setRemoteParticipant(null);
            readyRef.current = false;
            setIsConnected(false);
        }
    }, []);

    const disconnect = useCallback(() => {
        if (roomRef.current) {
            roomRef.current.disconnect();
            roomRef.current = null;
        }
        setIsConnected(false);
        setConnectionState('disconnected');
        setRemoteParticipant(null);
    }, []);

    useEffect(() => {
        // This effect now correctly handles the connection lifecycle.
        if (!sessionId) {
            return;
        }

        const connectToRoom = async () => {
            setIsConnecting(true);
            setError(null);
            
            try {
                const roomName = `session-${sessionId}`;
                const identity = `frontend-client-${sessionId}`;

                console.log(`[${sessionId}] Connecting to Twilio room: ${roomName}`);

                const backendUrl = getBrowserBackendUrl();
                const response = await fetch(`${backendUrl}/api/twilio/token`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sessionId, roomName, identity })
                });

                if (!response.ok) throw new Error(`Token fetch failed: ${response.statusText}`);
                const token = await response.text();
                
                const { connect: twilioConnect } = await import('twilio-video');
                const room = await twilioConnect(token, { 
                    name: roomName,
                    tracks: [] // Don't create any local tracks - we're only receiving video
                });

                console.log(`[${sessionId}] Connected to Twilio room. Participants: ${room.participants.size}`);

                roomRef.current = room;
                setConnectionState('connected');

                // Check for existing participants (this is the key fix for the race condition)
                room.participants.forEach(participant => {
                    console.log(`[${sessionId}] Found existing participant: ${participant.identity}`);
                    handleParticipantConnected(participant);
                });

                // If a remote browser participant is already present with a video track, mark ready
                const remoteBrowserParticipant = Array.from(room.participants.values())
                    .find(p => p.identity.startsWith('remote-browser-'));
                if (remoteBrowserParticipant) {
                    const videoPub = Array.from(remoteBrowserParticipant.videoTracks.values()).find(v => v.track);
                    if (videoPub?.track) {
                        handleTrackSubscribed(videoPub.track);
                    }
                }

                room.on('participantConnected', handleParticipantConnected);
                room.on('participantDisconnected', handleParticipantDisconnected);
                room.on('disconnected', () => disconnect());

            } catch (err) {
                console.error(`[${sessionId}] Connection error:`, err);
                setError(err.message);
                disconnect();
            } finally {
                setIsConnecting(false);
            }
        };

        connectToRoom();

        return () => {
            disconnect();
        };
    }, [sessionId, disconnect, handleParticipantConnected, handleParticipantDisconnected, handleTrackSubscribed]);
    
    const attachVideo = useCallback((element) => {
        videoRef.current = element;
        if (remoteParticipant) {
            remoteParticipant.tracks.forEach(publication => {
                if (publication.track) {
                    publication.track.attach(videoRef.current);
                }
            });
        }
    }, [remoteParticipant]);

    // Input handlers remain the same, but they now rely on the parent for the socket.
    const sendUserInput = (socket, command) => {
        if (socket?.connected) {
            // The agent expects the command data to be nested under a 'command' key.
            socket.emit('input-command', { sessionId, command });
        }
    };
    
    return {
        isConnected,
        isConnecting,
        error,
        connectionState,
        attachVideo,
        sendUserInput, // Expose the generic sender
        disconnect, // Expose the disconnect function
    };
};

export default useWebRTC; 